/**
 * Location field — a `.value`/`@change` form field over the workspace's
 * venue catalog, dropping in wherever picking a location needs to be a
 * real, mapped grid of venues rather than a hidden `<schmancy-select>`
 * option set. Renders its own `label` (like `schmancy-input`/`schmancy-select`)
 * above the trigger, which IS the field: an empty-state button when `.value`
 * is "" (no venue picked), or a compact tile naming the picked venue (name +
 * "within {parent}" / address, resolved via `locationsPageView`) with an
 * edit affordance when set. Clicking either opens the picker — an overlay
 * carrying a pinned header over a scrolling grid of map-hero cards, the same
 * card `location-list` renders, with the picked one marked; choosing a card sets
 * `.value` and fires `@change` — the same convention `schmancy-select`/
 * `schmancy-input` use, so it drops in identically. `placeholder` labels the
 * empty-state trigger, titles the overlay and names it to a screen reader,
 * defaulting to "Pick a venue", so a caller passes one only to say something
 * else.
 *
 * Two properties speak about empty, in two moods, in two slots that never
 * meet on screen. `emptyHint` STATES what leaving the field empty means
 * ("Uses the event's own venue.") under the empty-state trigger, and renders
 * only while `.value` is in fact "". `emptyLabel` NAMES the overlay row that
 * CHOOSES empty ("Use the event's own venue") and is the whole of that row,
 * which exists only where a caller supplies the label and a value stands to
 * be cleared — so a caller whose own validation refuses an empty pick never
 * offers a state its form rejects. Both are omitted where empty needs no
 * explanation. Zero-I/O — reads the venue catalog and its load state, emits
 * an id, never writes.
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
// Side-effect: registers <schmancy-visually-hidden>.
import "@mhmo91/schmancy/visually-hidden";
import { html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { merge, Subject, take, takeUntil } from "rxjs";
import { LocalizeMixin } from "../i18n/localize.ts";
import { locationsStatus } from "../storefront/locations.state.ts";
import { workspaceMapsApiKey } from "../workspace/workspace.state.ts";
import {
	locationMapMarkerColor,
	locationsPageView,
	resolveLocationRow,
} from "./locations.state.ts";

@schmancyElement("select-location")
export class SelectLocation extends LocalizeMixin(SchmancyElement) {
	/** The selected location id — "" means none. */
	@property({ type: String }) value = "";
	/** The empty-state trigger's label and the overlay list's title. Empty falls
	 *  to "Pick a venue", resolved where it is read rather than at construction,
	 *  so a locale switch reaches it. */
	@property({ type: String }) placeholder = "";
	/** Rendered above the trigger, like schmancy-input/schmancy-select's own label. Omitted when empty. */
	@property({ type: String }) label = "";
	/** What leaving this field empty MEANS, e.g. "Uses the event's own venue." —
	 *  a statement, rendered under the empty-state trigger. Omitted (null) when a
	 *  caller needs no such explanation. */
	@property({ type: String }) emptyHint: string | null = null;
	/** Names the overlay row that CHOOSES empty, e.g. "Use the event's own
	 *  venue" — an instruction, and the whole of that row. Omitted (null) by a
	 *  caller whose own validation refuses an empty pick, which is what takes
	 *  the row away. */
	@property({ type: String }) emptyLabel: string | null = null;

	private openPicker(): void {
		const picked$ = new Subject<string>();
		picked$.pipe(take(1), takeUntil(this.disconnecting)).subscribe((id) => {
			if (id === this.value) return;
			this.value = id;
			this.dispatchEvent(
				new CustomEvent<{ value: string }>("change", {
					detail: { value: id },
					bubbles: true,
					composed: true,
				}),
			);
		});
		const title =
			this.placeholder ||
			this.t("admin.locations.selectLocation.defaultPlaceholder");
		// Dismissed via unsubscribe (picked$/disconnecting), never a direct
		// close() call — silent so its history entry never exists to pop, since
		// an unsubscribe-driven pop fires a real popstate event that any
		// still-open ancestor overlay's own popstate listener also reacts to.
		show(
			() => {
				const { rows } = locationsPageView.get();
				const markerColor = locationMapMarkerColor.get();
				// The caller's own label is what offers the choice at all, so a
				// picker whose consumer refuses an empty pick shows no way to make
				// one.
				const offersEmpty = !!this.emptyLabel && this.value !== "";
				const arms = {
					loading: () => html`
						<div class="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6 px-4 pb-4 pt-1 md:px-6 md:pb-6">
							${repeat(
								// Six, not five: it fills exactly three rows at two columns
								// and two at three, so neither width shows a ragged half-row.
								[0, 1, 2, 3, 4, 5] as const,
								(i) => i,
								() => html`
									<div class="flex flex-col gap-2">
										<schmancy-skeleton class="aspect-[4/3] w-full rounded-2xl"></schmancy-skeleton>
										<schmancy-skeleton shape="text" class="w-3/4"></schmancy-skeleton>
										<schmancy-skeleton shape="text" class="w-1/2"></schmancy-skeleton>
									</div>
								`,
							)}
						</div>
					`,
					error: () => html`
						<div class="flex flex-col items-start gap-3 px-4 py-8 md:px-6">
							<schmancy-icon class="text-3xl text-error-default">error</schmancy-icon>
							<schmancy-typography type="title" token="sm">${this.t("admin.locations.list.errorTitle")}</schmancy-typography>
							<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
								${this.t("admin.locations.list.errorBody")}
							</schmancy-typography>
						</div>
					`,
					denied: () => html`
						<div class="flex flex-col items-start gap-3 px-4 py-8 md:px-6">
							<schmancy-icon class="text-3xl text-error-default">lock</schmancy-icon>
							<schmancy-typography type="title" token="sm">${this.t("admin.locations.list.deniedTitle")}</schmancy-typography>
							<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
								${this.t("admin.locations.list.deniedBody")}
							</schmancy-typography>
						</div>
					`,
					// The empty-catalog line hangs off this arm, not off a bare row
					// count: the catalog state starts [] and stays [] until the first
					// snapshot lands, and stays [] for as long as a listener error
					// keeps retrying, so the count alone cannot tell an empty catalog
					// from one nobody knows yet.
					ready: () =>
						when(
							rows.length === 0,
							() => html`
								<schmancy-typography type="body" token="sm" class="text-surface-onVariant px-4 py-8 md:px-6">
									${this.t("admin.locations.selectLocation.noLocationsYet")}
								</schmancy-typography>
							`,
							() => html`
								<div
									role="radiogroup"
									aria-label=${title}
									class="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6 px-4 pb-4 pt-1 md:px-6 md:pb-6"
								>
									${repeat(
										rows,
										(r) => r.id,
										(r) => {
											// A room with no pin of its own is physically at its
											// mapped ancestor's coordinates (locations.state.ts's
											// effectiveGeo) — the hero rides that, not the row's
											// own (possibly absent) geo.
											const geo = r.effectiveGeo;
											const isInheritedGeo = !r.geo && !!geo;
											// Precomputed outside the `when()` closures below so neither
											// needs to re-narrow `geo` past a function boundary.
											// Static Maps refuses a keyless request, so an organization with no
											// key renders the placeholder rather than a broken image.
											const mapsApiKey = workspaceMapsApiKey.get();
											const mapImageUrl =
												geo && mapsApiKey
													? `https://maps.googleapis.com/maps/api/staticmap?center=${geo.lat},${geo.lng}&zoom=15&size=600x400&scale=2&markers=color:0x${markerColor}|${geo.lat},${geo.lng}&key=${mapsApiKey}`
													: null;
											const isSelected = r.id === this.value;
											return html`
												<schmancy-card
													interactive
													role="radio"
													aria-checked=${isSelected ? "true" : "false"}
													class="group overflow-hidden rounded-2xl"
													@click=${() => picked$.next(r.id)}
												>
													<div class="relative flex h-full flex-col gap-3 rounded-2xl">
														<div class="relative aspect-[4/3] w-full bg-surface-containerHigh">
															${when(
																mapImageUrl,
																(url) => html`
																	<img
																		src=${url}
																		alt=""
																		class="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
																	/>
																	${when(
																		isInheritedGeo,
																		() => html`
																			<schmancy-surface
																				type="container"
																				opacity="solid"
																				elevated
																				class="absolute bottom-2 left-2 z-10 rounded-full px-2.5 py-1"
																			>
																				<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
																					${this.t("admin.locations.common.withinPrefix", { name: r.geoInheritedFromName ?? "" })}
																				</schmancy-typography>
																			</schmancy-surface>
																		`,
																	)}
																`,
																() => html`
																	<div class="flex h-full w-full flex-col items-center justify-center gap-1 px-4 text-center">
																		<schmancy-icon class="text-5xl text-surface-onVariant/40">
																			${r.withinName ? "meeting_room" : "location_on"}
																		</schmancy-icon>
																		${when(
																			r.withinName,
																			() => html`
																				<schmancy-typography type="body" token="xs" class="text-surface-onVariant/60">
																					${this.t("admin.locations.common.withinPrefix", { name: r.withinName ?? "" })}
																				</schmancy-typography>
																			`,
																		)}
																	</div>
																`,
															)}

														</div>
														<div class="flex flex-col gap-1 px-3 pb-3">
${when(
	isSelected,
	() => html`
																	<schmancy-typography type="label" token="md" class="text-surface-onVariant">
																		<schmancy-icon class="text-primary-default">check</schmancy-icon>
																	</schmancy-typography>
																`,
)}
															<schmancy-typography type="title" token="sm" class="truncate">${r.name}</schmancy-typography>
															<schmancy-typography type="body" token="sm" class="text-surface-onVariant truncate">
																${r.address}
															</schmancy-typography>
															${when(
																// Already conveyed once, in the hero: the map-overlay
																// caption when the pin is inherited, the icon caption
																// when there's no pin anywhere. This context line earns its
																// keep only for the remaining case — a room with its
																// own pin that's still nested under a parent.
																r.withinName && r.geo,
																() => html`
																	<schmancy-typography type="label" token="md" class="text-surface-onVariant self-start">
																		${this.t("admin.locations.common.withinPrefix", { name: r.withinName ?? "" })}
																	</schmancy-typography>
																`,
															)}
														</div>
														${when(
															isSelected,
															() => html`
																<!-- Its own layer, not a ring on the card or on
																     this wrapper: an inset ring is an inner
																     box-shadow, which paints with the element's
																     background and so sits under the opaque
																     full-bleed hero. On the host a ring-* would
																     take the place of the elevation shadow and an
																     outline-* would outrank the card's own
																     :focus-visible outline. -->
																<div class="pointer-events-none absolute inset-0 z-10 rounded-2xl inset-ring-2 inset-ring-primary-default"></div>
															`,
														)}
													</div>
												</schmancy-card>
											`;
										},
									)}
								</div>
							`,
						),
				} satisfies Record<typeof locationsStatus.value, () => TemplateResult>;
				const announcement = {
					loading: this.t("admin.locations.list.loadingAriaLabel"),
					error: this.t("admin.locations.list.errorTitle"),
					denied: this.t("admin.locations.list.deniedTitle"),
					ready: `${rows.length} ${this.t(
						rows.length === 1
							? "admin.locations.list.countSingular"
							: "admin.locations.list.countPlural",
					)}`,
				} satisfies Record<typeof locationsStatus.value, string>;
				return html`
					<!-- The picker declares its own measure, because show() names no
					     footprint and an anchored surface shrinks to its content: a
					     w-full root would resolve against the very track its own card
					     grid just sized, leaving a two-venue catalog as a narrow pill.
					     max-w-full hands the width back to the overlay's viewport cap
					     on a phone. -->
					<div class="flex h-full w-2xl max-w-full flex-col">
						<!-- Pinned: an empty catalog is exactly when the empty choice is
						     the only answer left, so it must outlive a scrolled grid. It
						     also stays outside the radiogroup — offersEmpty renders it
						     only when a value IS set, so it clears rather than competes,
						     and aria-checked would be a false claim. -->
						<div class="flex shrink-0 flex-col gap-3 px-4 pt-4 pb-3 md:px-6 md:pt-5">
							<schmancy-typography type="title" token="md">${title}</schmancy-typography>
							${when(
								offersEmpty,
								() => html`
									<schmancy-list surface="transparent">
										<schmancy-list-item rounded @click=${() => picked$.next("")}>
											<schmancy-icon slot="leading" aria-hidden="true">location_off</schmancy-icon>
											<schmancy-typography type="title" token="sm" class="text-surface-onVariant">
												${this.emptyLabel}
											</schmancy-typography>
										</schmancy-list-item>
									</schmancy-list>
									<schmancy-divider></schmancy-divider>
								`,
							)}
						</div>
						<div ${overflowWithin()} class="min-h-0 flex-1">
							<!-- Outside the arm it announces, so the element itself
							     survives every arm swap: a live region inserted with its
							     text already in place is not announced — only a change
							     inside one that was already there is. -->
							<schmancy-visually-hidden role="status" aria-live="polite">
								${announcement[locationsStatus.value]}
							</schmancy-visually-hidden>
							${arms[locationsStatus.value]()}
						</div>
					</div>
				`;
			},
			{
				historyStrategy: "silent",
				ariaLabel: title,
			},
		)
			.pipe(takeUntil(merge(this.disconnecting, picked$)))
			.subscribe();
	}

	override render(): TemplateResult {
		const selected = this.value ? resolveLocationRow(this.value) : undefined;

		return html`
			<div class="flex flex-col gap-2">
				${when(
					this.label,
					() => html`
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">${this.label}</schmancy-typography>
					`,
				)}
				${
					selected
						? html`
							<schmancy-list surface="transparent">
								<schmancy-list-item rounded @click=${() => this.openPicker()}>
									<div class="min-w-0 flex-1">
										<schmancy-typography type="title" token="sm">${selected.name}</schmancy-typography>
										<schmancy-typography type="body" token="sm" class="text-surface-onVariant truncate">
											${selected.withinName ? this.t("admin.locations.common.withinPrefix", { name: selected.withinName }) : selected.address}
										</schmancy-typography>
									</div>
									<schmancy-icon slot="trailing" aria-hidden="true">edit</schmancy-icon>
								</schmancy-list-item>
							</schmancy-list>
						`
						: html`
							<schmancy-button variant="outlined" class="self-start" @click=${() => this.openPicker()}>
								<schmancy-icon slot="prefix" aria-hidden="true">location_on</schmancy-icon>
								${this.placeholder || this.t("admin.locations.selectLocation.defaultPlaceholder")}
							</schmancy-button>
							<!-- Gated on the value itself, not on the trigger it sits under:
							     an id that resolves to nothing — a catalog still loading, a
							     venue since deleted — draws this same trigger while an
							     override IS set, and the hint would then state an
							     inheritance the stored value contradicts. -->
							${when(
								this.emptyHint && this.value === "",
								() => html`
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant">${this.emptyHint}</schmancy-typography>
								`,
							)}
						`
				}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"select-location": SelectLocation;
	}
}
